import type {
  StoreFilterCategory,
  StoreFiltersResponse,
  StoreProductSort,
  StoreStockFilter,
} from '@/composables/useStoreProducts';
import {
  getStoreProductSortValue,
  STORE_PRODUCTS_PER_PAGE,
} from '@/composables/useStoreProducts';
import type { PaginatedListMeta } from '@/types/PaginatedListMeta';
import type { PaginatedListResponse } from '@/types/PaginatedListResponse';
import type { Product } from '@/types/Product';

export type MarketplaceProductFilters = {
  categoryId?: number | null;
  subcategoryId?: number | null;
  priceRange?: [number, number] | null;
  stock?: StoreStockFilter | null;
  merchantId?: number | null;
};

export type MarketplaceFilterQueryOptions = {
  search?: string;
  filters?: MarketplaceProductFilters;
};

export type MarketplaceProductQueryOptions = MarketplaceFilterQueryOptions & {
  page?: number;
  sort?: StoreProductSort;
};

const buildMarketplaceFilterParams = ({
  search,
  filters,
}: MarketplaceFilterQueryOptions) => {
  const normalizedSearch = search?.trim();

  return [
    ...(normalizedSearch
      ? [`filter[search]=${encodeURIComponent(normalizedSearch)}`]
      : []),
    ...(filters?.categoryId ? [`filter[category]=${filters.categoryId}`] : []),
    ...(filters?.subcategoryId
      ? [`filter[subcategory]=${filters.subcategoryId}`]
      : []),
    ...(filters?.priceRange
      ? [
          `filter[price_range]=${filters.priceRange[0]},${filters.priceRange[1]}`,
        ]
      : []),
    ...(filters?.stock ? [`filter[stock]=${filters.stock}`] : []),
    ...(filters?.merchantId ? [`filter[merchant]=${filters.merchantId}`] : []),
  ];
};

export const buildMarketplaceFiltersQuery = (
  options: MarketplaceFilterQueryOptions = {}
) => {
  const params = buildMarketplaceFilterParams(options);
  return `marketplace/filters${params.length ? `?${params.join('&')}` : ''}`;
};

export const buildMarketplaceProductQuery = ({
  page = 1,
  search,
  sort = '',
  filters,
}: MarketplaceProductQueryOptions = {}) => {
  const sortValue = getStoreProductSortValue(sort);
  const params = [
    `page=${page}`,
    `per_page=${STORE_PRODUCTS_PER_PAGE}`,
    'include=photos,category,seller.accountable.logo,seller.accountable.coverPhoto',
    ...buildMarketplaceFilterParams({ search, filters }),
    ...(sortValue ? [`sort=${sortValue}`] : []),
  ];

  return `marketplace/products?${params.join('&')}`;
};

export default function useMarketplaceProducts() {
  const { data, pending, error, execute } =
    useGetData<PaginatedListResponse<Product>>();
  const {
    data: filterData,
    pending: filtersPending,
    error: filtersError,
    execute: executeFilters,
  } = useGetData<StoreFiltersResponse>();

  const fallbackMeta = (total = 0): PaginatedListMeta => ({
    current_page: 1,
    last_page: Math.max(1, Math.ceil(total / STORE_PRODUCTS_PER_PAGE)),
    total,
  });

  const fetchProducts = async (
    options: MarketplaceProductQueryOptions = {}
  ) => {
    await execute(buildMarketplaceProductQuery(options));
  };

  const fetchFilters = async (options: MarketplaceFilterQueryOptions = {}) => {
    await executeFilters(buildMarketplaceFiltersQuery(options));
  };

  const products = computed(() => data.value?.data ?? []);
  const meta = computed(
    () => data.value?.meta ?? fallbackMeta(products.value.length)
  );
  const filterCategories = computed<StoreFilterCategory[]>(
    () => filterData.value?.data.categories ?? []
  );
  const priceRange = computed<[number, number]>(() => {
    const range = filterData.value?.data.price_range;
    return [range?.min ?? 0, range?.max ?? 0];
  });
  const stockCounts = computed(
    () => filterData.value?.data.stock ?? { in_stock: 0, out_of_stock: 0 }
  );
  const facetTotal = computed(() => filterData.value?.data.total ?? 0);

  return {
    pending,
    error,
    filtersPending,
    filtersError,
    products,
    meta,
    filterCategories,
    priceRange,
    stockCounts,
    facetTotal,
    fetchProducts,
    fetchFilters,
  };
}
